/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { useLazyGetUserInfoQuery } from '@/store/apis';
import { Button, FlexBox, IllustratedMessage } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';

import axios from 'axios';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { browserLocale } from '../commons';
import { useTranslations } from '../hooks/useTranslations';
import { actions } from '../store/Products';

import { checkForAuthCallback } from '../utils/authUtils';
import { setLanguage } from '@ui5/webcomponents-base/dist/config/Language.js';
import * as storageUtils from './../utils/storageUtils';
import { useAppDispatch, useAppSelector } from '@/hooks';
import useOpen from '@/hooks/useOpen';
import SignUp from '@/components/signUp/SignUp';
import { loadingActions } from '@/store/loadingSlice';
import { isFallbackLanguage } from '@/utils/langUtils';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const queryParams = new URLSearchParams(location.search);
    const { t } = useTranslations();
    const [isOpen, toggle] = useOpen();

    const userSettings = useAppSelector(state => state.productList.userSettings);
    const [getUserInfo, { data: userInfo, error, isSuccess: isGetUserInfoSuccess, isError: isGetUserInfoError }] = useLazyGetUserInfoQuery();

    useEffect(() => {
        const handleUserInfo = async () => {
            if (isGetUserInfoError) {
                console.error('Error fetching user info:', error);
                return;
            }

            if (isGetUserInfoSuccess) {
                storageUtils.setItem('user', userInfo);
                const hitUrl = storageUtils.getItem('hitUrl');
                dispatch(
                    actions.setUserSettings({
                        ...userSettings,
                        locale: userInfo?.locale ?? browserLocale,
                        selectedLocal: userInfo?.locale ?? browserLocale,
                    })
                );
                const locale = userInfo?.locale ?? browserLocale;
                await setLanguage(isFallbackLanguage(locale.toLowerCase()) ? 'fallback' : locale);
                navigate(hitUrl || '/');
            }
        };

        handleUserInfo();
    }, [isGetUserInfoSuccess, isGetUserInfoError]);

    const getAuthToken = async () => {
        const code = storageUtils.getItem('code');
        const state = storageUtils.getItem('state');
        if (code && state) {
            try {
                dispatch(loadingActions.startLoading());
                const response = await axios.get(`/fre_api/v1/token`, {
                    params: {
                        code,
                        state,
                    },
                });
                storageUtils.setItem('token', response.data.access_token);
                storageUtils.setItem('refreshToken', response.data.refresh_token);
            } catch (error) {
                console.error('Error fetching token:', error);
            } finally {
                dispatch(loadingActions.stopLoading());
            }
        }
    };

    const getTokenAndUserInfo = async () => {
        await getAuthToken();
        const token = storageUtils.getItem('token');
        if (token) {
            await getUserInfo();
        }
    };

    useEffect(() => {
        (async () => {
            if (checkForAuthCallback(queryParams)) {
                const token = storageUtils.getItem('token');
                if (token) {
                    await getUserInfo();
                    return;
                }
            }
            await getTokenAndUserInfo();
        })();
    }, []);

    const handleLogin = () => {
        dispatch(loadingActions.startLoading());
        window.location.href = `/oauth2/authorization/ias`;
    };

    return (
        <div style={{ height: '100vh' }} data-sap-ui-fastnavgroup="true">
            <IllustratedMessage name="Connection" titleText={t('LOGIN_TITLE_TEXT')} subtitleText={t('LOGIN_SUBTITLE_TEXT')}>
                <FlexBox direction="Row" style={{ gap: ThemingParameters.sapContent_Gap }}>
                    <Button onClick={handleLogin} design="Emphasized" data-testid="signin-button">
                        {t('LOGIN_BUTTON')}
                    </Button>
                    <Button onClick={toggle} design="Default" data-testid="signup-button">
                        {t('SIGNUP')}
                    </Button>
                </FlexBox>
            </IllustratedMessage>

            {isOpen && <SignUp onClose={toggle} />}
        </div>
    );
};
export default LoginPage;
